// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect } from 'react';

import type { MachineElement } from '@evolvemep/api-ts';

import { ForgeViewerFromModel } from 'components/Autodesk/ForgeViewerFromModel';
import { useAutodeskExtensionStoreSnapshot } from 'components/Autodesk/useAutodeskExtensionStore';
import { isNotNil } from 'helpers/isNotNil';
import type { BendModelBrowserState } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/useBendModelBrowserState';
import { useViewerMachineElement } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/useViewerMachineElement';
import type { SelectedModelState } from 'modules/Shop/ModelBrowser/useModelSelection';

type Props = {
  element: MachineElement;
  model: SelectedModelState;
  previewState: BendModelBrowserState;
};

/** Renders ForgeViewer for a given MachineElement and model */
export const BendModelBrowser = ({ element, model, previewState }: Props) => {
  const { spoolDbIds, elementDbIds } = useViewerMachineElement(element);
  const { commands } = useAutodeskExtensionStoreSnapshot('apsViewer');
  const {
    fitToView: shouldFitToView,
    ghosting,
    isolating,
    spoolColor,
    spoolColorEnabled,
    elementColor,
    elementColorEnabled,
  } = previewState;

  useEffect(() => {
    commands?.setGhosting(ghosting);
  }, [commands, ghosting]);

  useEffect(() => {
    const dbIdsToIsolate = isolating === 'spool' ? (spoolDbIds ?? []) : isolating === 'element' ? elementDbIds : [];
    commands?.isolate(dbIdsToIsolate, shouldFitToView);
  }, [isolating, spoolDbIds, elementDbIds, commands, shouldFitToView]);

  useEffect(() => {
    commands?.clearThemingColors();
    if (isNotNil(spoolDbIds) && spoolColorEnabled) {
      commands?.setThemingColor(spoolDbIds, spoolColor);
    }
    if (isNotNil(elementDbIds) && elementColorEnabled) {
      commands?.setThemingColor(elementDbIds, elementColor, true);
    }
  }, [commands, spoolDbIds, spoolColorEnabled, spoolColor, elementDbIds, elementColorEnabled, elementColor]);

  return <ForgeViewerFromModel urn={model.urn} showSpoolMenu={false} />;
};
